/**
 * useProjectTree.js - the Projects / Opportunities tables' "Group by main
 * project" (10/14/26): the records the table shows (filtered and sorted, as
 * before) drawn as main project > sub-project > PO line (data/projectHierarchy).
 *
 *   available the table offers it (view.grouping - not the GLOBAL KSA tab)
 *             and some record has a Main project set (by hand - with none,
 *             the table is exactly as before, no switch)
 *   on        available, and the view's look.group is not switched off
 *             (saved with the view; on by default)
 *   items     the rows to draw, [{ node, depth, last, open, partial }], or
 *             null when off (the table draws its records flat, as before)
 *   isOpen / toggle / expandAll / collapseAll
 *             which groups are open - kept per table on this computer (a
 *             per-person convenience, never a record). A search opens every
 *             group with a match, so a hit is never hidden in a closed group.
 *   mainLabel(record)  the export's Main project column
 *
 * The groups are built over the table's own records (`source`), so a filter
 * never changes what a group is - only which of its records show; `lookup`
 * (every record, every year) finds a Main project outside the year shown.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { anyLinked, buildGroups, flattenTree, groupKeys, mainLabelOf, treeOf } from '../../../data/projectHierarchy';

const KEY = (tableKey) => `globalops_tree_open:${tableKey || 'table'}`;
function readOpen(tableKey) {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY(tableKey)) || '[]');
    return new Set(Array.isArray(v) ? v.map(String) : []);
  } catch {
    return new Set();
  }
}

/** 'main' / 'sub' when `id` is the record a main project / sub-project group row stands for, else null. */
function levelOfRecord(nodes, id) {
  for (const n of nodes || []) {
    if (n.type !== 'group') continue;
    if (String(n.mainRecordId) === String(id)) return n.level;
    const inner = levelOfRecord(n.children, id);
    if (inner) return inner;
  }
  return null;
}

export function useProjectTree({ tableKey, view, rows, source, lookup, search = '' }) {
  const linked = useMemo(() => anyLinked(source), [source]);
  const available = !!(view && view.grouping && linked);
  const on = available && (!view.look || view.look.group !== false);
  const groups = useMemo(() => (on ? buildGroups(source || [], { lookup: lookup || source || [] }) : null), [on, source, lookup]);
  const nodes = useMemo(() => (groups ? treeOf(rows || [], groups) : null), [groups, rows]);
  // How groups start is the view's (Customize > Group rows, tree.open):
  //   remember   the groups this person left open, kept on this computer
  //   expanded   every group open (`flip` holds the ones closed since)
  //   collapsed  every group closed (`flip` holds the ones opened since)
  const mode = (view && view.tree && view.tree.open) || 'remember';
  const [flip, setFlip] = useState(() => (mode === 'remember' ? readOpen(tableKey) : new Set()));
  const [seenMode, setSeenMode] = useState(mode);
  if (seenMode !== mode) {
    // the view's start changed: start over from it
    setSeenMode(mode);
    setFlip(mode === 'remember' ? readOpen(tableKey) : new Set());
  }
  useEffect(() => {
    if (mode !== 'remember') return;
    try {
      window.localStorage.setItem(KEY(tableKey), JSON.stringify([...flip].slice(-400)));
    } catch {
      /* a convenience only */
    }
  }, [flip, tableKey, mode]);
  const searching = !!String(search || '').trim();
  const isOpen = useCallback(
    (k) => searching || (mode === 'expanded' ? !flip.has(String(k)) : flip.has(String(k))),
    [flip, searching, mode]
  );
  const items = useMemo(() => (nodes ? flattenTree(nodes, isOpen) : null), [nodes, isOpen]);
  const toggle = useCallback(
    (k) =>
      setFlip((cur) => {
        const next = new Set(cur);
        if (next.has(String(k))) next.delete(String(k));
        else next.add(String(k));
        return next;
      }),
    []
  );
  const allKeys = useMemo(() => (nodes ? groupKeys(nodes) : []), [nodes]);
  const expandAll = useCallback(() => setFlip((cur) => (mode === 'expanded' ? new Set() : new Set([...cur, ...allKeys]))), [allKeys, mode]);
  const collapseAll = useCallback(
    () => setFlip((cur) => (mode === 'expanded' ? new Set([...cur, ...allKeys]) : new Set())),
    [allKeys, mode]
  );
  const setOn = useCallback(
    (v) => {
      if (view && view.setLook) view.setLook({ group: !!v });
    },
    [view]
  );
  const mainLabel = useCallback((r) => (groups ? mainLabelOf(groups, r) : ''), [groups]);
  const groupCount = nodes ? nodes.filter((n) => n.type === 'group').length : 0;
  return {
    available,
    on,
    groups,
    nodes,
    items,
    isOpen,
    toggle,
    expandAll,
    collapseAll,
    setOn,
    mainLabel,
    groupCount,
    searching,
    anyOpen: searching || allKeys.some((k) => isOpen(k)),
    // the export's row colour: a main project's / sub-project's own record (Customize > Group rows)
    levelOf: (id) => levelOfRecord(nodes, id),
  };
}
